import React from 'react';
import { Button, Modal, ModalProps } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Form, Formik, useFormikContext } from 'formik';
import { object, string, boolean } from 'yup';
import { map } from 'lodash';

import { FormGroupSelect, FormGroupCheck } from '@components/Base';
import { CashFlows, PolicyCommissionInvoiceList } from '@models/PolicyCommissionInvoice';
import { mergeInitialValues } from '@utils/formik';

import { PolicyCommissionInvoiceCreatePayload } from './action';

export type PolicyCommissionInvoiceFormValues = Pick<PolicyCommissionInvoiceCreatePayload, 'cashFlow' | 'insurerPaid' | 'frontlinePaid'>;

const GROUP_PROPS = { className: 'mb-3' };

type FormFormProps = Pick<ModalProps, 'onHide'> & {
  isEdit?: boolean;
};

const FormForm = ({ onHide, isEdit = false }: FormFormProps) => {
  const { t } = useTranslation();
  const { initialValues } = useFormikContext<PolicyCommissionInvoiceFormValues>();

  return (
    <Form>
      <Modal.Header closeButton>
        <Modal.Title>{isEdit ? 'Edit Invoice' : 'New Invoice'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <FormGroupSelect
          asRow
          label="Cash flow"
          name="cashFlow"
          required
          groupProps={GROUP_PROPS}
          disabled={isEdit}
        >
          <option></option>
          {map(CashFlows, (cashFlow) => (
            <option key={cashFlow} value={cashFlow}>
              {cashFlow}
            </option>
          ))}
        </FormGroupSelect>
        <FormGroupCheck
          asRow
          id="a"
          label="Insurer paid"
          name="insurerPaid"
          groupProps={GROUP_PROPS}
          defaultChecked={initialValues.insurerPaid}
        />
        <FormGroupCheck
          asRow
          id="b"
          label="Frontline paid"
          name="frontlinePaid"
          groupProps={GROUP_PROPS}
          defaultChecked={initialValues.frontlinePaid}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          {t('confirmModal.cancel')}
        </Button>
        <Button type="submit" variant="primary">
          {t('common:save')}
        </Button>
      </Modal.Footer>
    </Form>
  );
}

const defaultValues: PolicyCommissionInvoiceFormValues = {
  cashFlow: '',
  insurerPaid: false,
  frontlinePaid: false,
};

type Props = ModalProps & {
  onSubmit: (values: PolicyCommissionInvoiceFormValues) => void;
  invoice: PolicyCommissionInvoiceList | null;
}

const PolicyCommissionInvoiceFormModal = ({ onSubmit, invoice, ...props }: Props) => {
  const { t } = useTranslation();
  const isEdit = !!invoice;
  const initialValues = mergeInitialValues(defaultValues, invoice);

  const validationSchema = object({
    cashFlow: string().required(t('validation:required')),
    insurerPaid: boolean().optional(),
    frontlinePaid: boolean().optional(),
  });

  return (
    <Modal {...props}>
      <Formik
        initialValues={initialValues}
        onSubmit={onSubmit}
        validationSchema={validationSchema}
      >
        <FormForm onHide={props.onHide} isEdit={isEdit} />
      </Formik>
    </Modal>
  )
};

export { PolicyCommissionInvoiceFormModal };
